import { createContext, useCallback, useContext,useEffect,useState } from 'react';
import { WallState } from '../types/stateProviders/WallState';
import { useFloorPlanDispatch, useFloorPlanState } from './FloorPlanStateProvider';

const WallStateContext = createContext<any | null>(null);
const WallDispatchContext = createContext<(newState:Partial<WallState>) => void>(() => {});

const isObjectShallowEqual = (obj1:WallState, obj2:WallState) => {
    if (!obj1 || !obj2) return false; // Check if either object is null or undefined
    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;
    for (const key in obj1) {
      if (obj1[key as keyof WallState] !== obj2[key as keyof WallState]) return false;
    }
    return true;
};

// WallStateProvider is a context provider that provides state to individual walls
// It will update the floorPlanState.walls[wallIndex] when the wallState changes
// It will NOT update the wallState[index] if floorPlanState.walls[wallIndex] is mutated
export const WallStateProvider:React.FC<{
    children:React.ReactNode,
    wallIndex:number,
    coordinates:{x1:number,x2:number,y1:number,y2:number}
}> = ({ children, wallIndex, coordinates }) => {

  const initialWallState:WallState = {
    lengthInPixels:0,
    lengthInInches:0,
    x1: coordinates.x1,
    x2: coordinates.x2,
    y1: coordinates.y1,
    y2: coordinates.y2,
    wallType:'',
    wallIndex,
    wallAxis: coordinates.x1 === coordinates.x2 ? 'y' : 'x'
  }

  const floorPlanDispatch = useFloorPlanDispatch()
  const floorPlanState = useFloorPlanState();

  //update the coordinates of the wall and pixel length when the floorplan corners change
  useEffect( () => {
    const xDiff = coordinates.x1 - coordinates.x2
    const yDiff = coordinates.y1 - coordinates.y2
    const lengthInPixels = Math.sqrt( xDiff**2 + yDiff**2 )
    updateWallState({
        x1:coordinates.x1,
        y1:coordinates.y1,
        x2:coordinates.x2,
        y2:coordinates.y2,
        lengthInPixels
    })
    //also update the length in inches if the pixelToInchRatio is set on corner change
    if(floorPlanState.pixelToInchRatio){
        updateWallState({lengthInInches: Math.round(lengthInPixels / floorPlanState.pixelToInchRatio)})
    }
  },[coordinates])

  const [wallState, setWallState] = useState<WallState>(initialWallState)
  const updateWallState = useCallback((newWallState:Partial<WallState>) => {
    // Update the wall state
    setWallState( (prevState:WallState) => {
        return {...prevState, ...newWallState}
    })
  },[wallState])

  useEffect( () => {
    // Calculate the length in inches when the pixelToInchRatio changes
    if(!floorPlanState.pixelToInchRatio) return
    updateWallState({lengthInInches: Math.round(wallState.lengthInPixels / floorPlanState.pixelToInchRatio)})
  },[floorPlanState.pixelToInchRatio])

  //update floorPlanState.walls[wallIndex] when wallState changes, only if the wallState is different
  useEffect( () => {
    if (!isObjectShallowEqual(wallState, floorPlanState.walls[wallIndex])) {
        const updatedWalls = [...floorPlanState.walls];
        updatedWalls[wallIndex] = wallState;
        floorPlanDispatch({ walls: updatedWalls });
    }
  },[wallState,floorPlanState.walls])

  useEffect( () => {

    // Set the wall type to the floorPlanState wall type on init
    updateWallState({ ...floorPlanState.walls[wallIndex] })
  },[])

  return (
    <WallStateContext.Provider value={wallState}>
      <WallDispatchContext.Provider value={updateWallState}>
        {children}
      </WallDispatchContext.Provider>
    </WallStateContext.Provider>
  );
}

export function useWallState():WallState {
  return useContext(WallStateContext);
}

export function useWallDispatch():React.Dispatch<Partial<WallState>>{
  return useContext(WallDispatchContext);
}